import { Controller, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import { Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateWorkItem_RequestTemplateAnswerInput,
  useConditionalTemplateQuestion_GetConditionalTemplateQuestionsForEstateWorkItemQuery,
  useEstateWorkItem_RequestTemplateMutation,
} from 'queries';

import FormTextField from 'common/form-text-field';
import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
    padding: '40px 0 0 0',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    alignSelf: 'center',
    textAlign: 'center',
  },
  title: {
    fontWeight: 700,
    fontSize: 32,
    marginBottom: 10,
  },
  contentContainer: {
    padding: theme.spacing(1),
    flex: 1,
    textAlign: 'left',
  },
  item: {
    marginLeft: 10,
    marginRight: 10,
  },
  actionButton: {
    width: '100%',
    borderRadius: '100px',
  },
  footerButtonsContainer: {
    padding: '30px 10px',
  },
  footerText: {
    marginBottom: theme.spacing(1),
    fontSize: '14px',
  },
}));

type FormValues = {
  requestTemplateAnswers: EstateWorkItem_RequestTemplateAnswerInput[];
};

type Props = {
  setShowModal: (value: boolean) => void;
  showModal: boolean;
  defaultValues?: FormValues;
};

export default function RequestTemplateModal({ setShowModal, showModal, defaultValues }: Props) {
  const history = useHistory();
  const classes = useStyles();
  const { portalId, estateWorkItemId } = useParams<Record<string, string>>();

  const onExitClick = () => {
    setShowModal(false);
  };

  const defaultValuesMap = (defaultValues?.requestTemplateAnswers || []).reduce(
    (acc, value) => {
      acc[value.questionId] = value.answer || '';
      return acc;
    },
    {} as Record<string, string>,
  );

  const [requestTemplate, { loading: requestTemplateExecuting }] =
    useEstateWorkItem_RequestTemplateMutation({
      onCompleted: () => {
        showToast({
          message: 'Template requested successfully',
          severity: NotificationSeverity.SUCCESS,
        });
        history.push(`/home/${portalId}/assigned-tasks`);
        setShowModal(false);
      },
      onError: (e) => {
        showAlert({
          message: 'Error requesting template: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const {
    data: conditionalTemplateQuestionsData,
    loading: conditionalTemplateQuestionsDataLoading,
  } = useConditionalTemplateQuestion_GetConditionalTemplateQuestionsForEstateWorkItemQuery({
    variables: { input: { portalId, estateWorkItemId } },
    onError: (e) => {
      showAlert({
        message: 'Error getting conditional template questions: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const conditionalTemplateQuestions = conditionalTemplateQuestionsData?.data || [];
  const requestTemplateAnswers = conditionalTemplateQuestions.map((question) => ({
    questionId: question.id,
    question: question.question,
    answer: defaultValuesMap[question.id] || '',
  }));

  const { handleSubmit, control } = useForm<FormValues>({
    values: { requestTemplateAnswers },
  });

  if (conditionalTemplateQuestionsDataLoading) {
    return (
      <Modal
        setShowModal={setShowModal}
        onExitClick={onExitClick}
        showModal={showModal}
        showActions={false}
        content={
          <div className={classes.container}>
            <Spinner />
          </div>
        }
        loading={false}
        actionDisabled={false}
        maxWidth="sm"
      />
    );
  }

  const submitForm = async (values: FormValues) => {
    const requestTemplateAnswers = values.requestTemplateAnswers.map((value) => ({
      questionId: value.questionId!,
      question: value.question!,
      answer: value.answer,
    }));

    requestTemplate({
      variables: {
        input: {
          id: estateWorkItemId,
          portalId,
          requestTemplateAnswers,
        },
      },
    });
  };

  return (
    <Modal
      setShowModal={setShowModal}
      onExitClick={onExitClick}
      showModal={showModal}
      showActions={false}
      content={
        <form className={classes.container} onSubmit={handleSubmit(submitForm)}>
          <div className={classes.headerContainer}>
            <Typography className={classes.title}>Let's Get You What You Need!</Typography>
            <Typography>
              The exact document you need is <strong>on the way</strong> and will be delivered{' '}
              <strong>in 1 business day.</strong> Confirm details below
            </Typography>
          </div>
          <div className={classes.contentContainer}>
            <Grid container>
              {conditionalTemplateQuestions.map((question, index) => (
                <Grid item xs={12} md={6} key={question.id}>
                  <div className={classes.item}>
                    <Controller
                      name={`requestTemplateAnswers.${index}.answer`}
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <FormTextField
                          label={question.question}
                          onChange={(evt) => onChange(evt.target.value)}
                          value={value}
                        />
                      )}
                    />
                  </div>
                </Grid>
              ))}
            </Grid>
          </div>
          <div className={classes.footerButtonsContainer}>
            <Typography className={classes.footerText}>Everything looks good?</Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.actionButton}
              type="submit"
              disabled={requestTemplateExecuting}
            >
              Request document!
            </Button>
          </div>
        </form>
      }
      loading={false}
      actionDisabled={false}
      maxWidth="md"
    />
  );
}
